import { Card } from '@nextui-org/react'
import {
  AboutUsIcon,
  BlogIcon,
  CareersIcon,
  CodeReviewAgent,
  CodingAgent,
  ContactIcon,
  DocumentationAgent,
  MigrationAgent,
  MuleSoftIntegrityIcon,
  ResourcesLibraryIcon,
  SecurityIcon,
  TestingAgent,
  UsecasesIcon
} from '../chat/campaignIcons'
import DataweaveGenerator from '../../assets/campaign/DataweaveGenerator.png'
import IntegrationGenerator from '../../assets/campaign/IntegrationGenerator.png'
import { FaArrowRightLong } from '@react-icons/all-files/fa6/FaArrowRightLong'

interface CampaignNavBarDropDownProps {
  title: string
  menuItems?: { label: string; href: string }[]
  url?: string
  type?: string
}

interface containerProps {
  title: string
  linkTitle?: string
  linkUrl?: string
  children: React.ReactNode
  className?: string
}

interface showCardProps {
  image: JSX.Element | string
  title: string
  description: string
  link?: string
  linkTitle?: string
  className?: string
}

interface platFormType {
  label: string
  href: string
  icon: JSX.Element
}

interface listItemType {
  label: string
  href: string
  description: string
  icon: JSX.Element
}

interface companyItemsType {
  title: string
  description: string
  link: string
  icon: JSX.Element
  badge: string | null
}

const platformMenuItems: platFormType[] = [
  {
    label: 'Coding Agents',
    href: 'https://www.curietech.ai/product/coding-agents',
    icon: <CodingAgent />
  },
  {
    label: 'Testing Agents',
    href: 'https://www.curietech.ai/product/testing-agents',
    icon: <TestingAgent />
  },
  {
    label: 'Documentation Agents',
    href: 'https://www.curietech.ai/product/documentation-agents',
    icon: <DocumentationAgent />
  },
  {
    label: 'Code Insight Agents',
    href: 'https://www.curietech.ai/product/code-insight-agents',
    icon: <CodeReviewAgent />
  },
  {
    label: 'Migration Agents',
    href: 'https://www.curietech.ai/product/migration-documentation-agent',
    icon: <MigrationAgent />
  }
]

const resourcesItems: listItemType[] = [
  {
    label: 'Blog',
    href: 'https://www.curietech.ai/blog',
    description: 'The latest industry news curated by our expert team.',
    icon: <BlogIcon />
  },
  {
    label: 'Resource Library',
    href: 'https://www.curietech.ai/resources',
    description: 'Explore to get the most out of our tools.',
    icon: <ResourcesLibraryIcon />
  },
  {
    label: 'Guide: MuleSoft Integration',
    href: 'https://www.curietech.ai/mulesoft-integration',
    description: 'Using AI to accelerate the development cycle and avoid human errors.',
    icon: <MuleSoftIntegrityIcon />
  }
]

const companyItems: companyItemsType[] = [
  {
    title: 'About us',
    description: "Learn about the company, our team, and what we're working towards.",
    icon: <AboutUsIcon />,
    link: 'https://www.curietech.ai/about',
    badge: null
  },
  {
    title: 'Careers',
    description: "We're always looking for talented people. Join our team!",
    icon: <CareersIcon />,
    link: 'https://www.curietech.ai/careers',
    badge: "We're hiring!"
  },
  {
    title: 'Contact',
    description: 'Contact us to learn how you can expedite your coding tasks.',
    icon: <ContactIcon />,
    link: 'https://www.curietech.ai/contact-us',
    badge: null
  }
]

const Container = ({ title, linkTitle, linkUrl, children, className }: containerProps) => {
  return (
    <div className={`mx-auto w-full ${className}`}>
      <div className='flex pt-2 pb-4 items-center justify-between'>
        <h3 className='font-semibold text-gray-500 text-sm'>{title}</h3>
        {linkTitle && linkUrl && (
          <h3
            onClick={() => (window.location.href = linkUrl)}
            className='flex font-semibold text-black text-sm cursor-pointer items-center gap-1'
          >
            {linkTitle} <FaArrowRightLong />
          </h3>
        )}
      </div>
      {children}
    </div>
  )
}

const ShowCard = ({ image, title, description, link, linkTitle, className }: showCardProps) => {
  return (
    <div
      onClick={() => {
        if (link) window.location.href = link
      }}
    >
      <Card
        shadow='none'
        radius='md'
        className={`bg-transparent border-0 cursor-pointer hover:bg-white ${className}`}
      >
        <div className='flex flex gap-2'>
          {typeof image === 'string' ? (
            <div className='rounded-lg w-48'>
              <img src={image} alt={title} />
            </div>
          ) : (
            <div className='rounded-lg'>{image}</div>
          )}
          <div className='flex flex-col gap-2'>
            <p className='font-medium text-lg pt-2'>{title}</p>
            <span className='font-xs opacity-[.65] text-base'>{description}</span>
            <p className='font-medium text-base flex items-center gap-1'>
              {linkTitle} <FaArrowRightLong />
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}

const renderPlatformDropdown = (platformMenuItems: platFormType[]) => {
  return (
    <>
      <Container
        title='Agents'
        linkTitle='Platform Overview'
        linkUrl='https://www.curietech.ai/product'
        className='pl-10 pr-10 pt-6 pb-10 bg-[#f6f4fe]'
      >
        <div className='grid grid-cols-2'>
          {platformMenuItems.map((item, index) => (
            <div key={index} className='hover:text-gray-100'>
              <a
                href={item.href}
                className='
                  flex
                  items-center
                  gap-2
                  p-3
                  text-lg
                  font-normal
                  text-neutral-800
                  rounded-lg
                  hover:bg-white
                '
              >
                {item.icon}
                <span>{item.label}</span>
              </a>
            </div>
          ))}
        </div>
      </Container>
      <Container title='Security' className='pt-6 pb-10 pl-10 pr-12 bg-[#ede8fb]'>
        <ShowCard
          image={<SecurityIcon />}
          title='Enterprise Grade Security'
          description='You are always protected and always in control of your code and IP.'
          link='https://www.curietech.ai/security'
          linkTitle='Learn More'
          className='p-3'
        />
      </Container>
    </>
  )
}

const renderUseCasesDropdown = () => {
  return (
    <Container title='Use Cases' className='pl-10 pr-10 pt-6 pb-10 bg-[#f6f4fe]'>
      <div className='grid grid-cols-2'>
        <div className='hover:text-gray-100'>
          <a
            key='use-cases-platform-migration'
            href='https://www.curietech.ai/use-cases/platform-migration'
            className='
              flex
              items-center
              gap-2
              p-3
              text-lg
              font-normal
              text-neutral-800
              rounded-lg
              hover:bg-white
            '
          >
            <UsecasesIcon />
            <div className='flex flex-col'>
              <p className='text-lg font-medium'>Platform Migration</p>
              <p className='text-base opacity-[.65]'>Ensure high quality code and</p>
              <p className='text-base opacity-[.65]'>faster delivery in less time.</p>
            </div>
          </a>
        </div>
      </div>
    </Container>
  )
}

const renderResourceDropdown = (resourceMenuItems: listItemType[]) => {
  return (
    <>
      <Container title='Use Cases' className='pl-10 pr-10 pt-6 pb-10 bg-[#f6f4fe]'>
        <div className='flex flex-col'>
          {resourceMenuItems.map((item, index) => (
            <div key={item.href ?? index} className='hover:text-gray-100'>
              <a
                href={item.href}
                className='
                  flex
                  items-center
                  gap-2
                  p-3
                  text-lg
                  font-normal
                  text-neutral-800
                  rounded-lg
                  hover:bg-white
                '
              >
                {item.icon}
                <div className='flex flex-col'>
                  <p className='text-lg font-medium'>{item.label}</p>
                  <p className='text-base opacity-[.65]'>{item.description}</p>
                </div>
              </a>
            </div>
          ))}
        </div>
      </Container>
      <Container
        title='Product Tours'
        linkTitle='All product tours'
        linkUrl='https://www.curietech.ai/resources?category=Product+Tour'
        className='pt-6 pb-10 pl-10 pr-12 bg-[#ede8fb]'
      >
        <ShowCard
          image={DataweaveGenerator}
          title='Dataweave Generator'
          description='Get a quick overview of our products and features.'
          link='https://www.curietech.ai/resources/dataweave-generator'
          linkTitle='See product tour'
          className='p-3'
        />
        <ShowCard
          image={IntegrationGenerator}
          title='Integration Generator'
          description='Get a quick overview of our products and features.'
          link='https://www.curietech.ai/resources/integration-generator'
          linkTitle='See product tour'
          className='p-3'
        />
      </Container>
    </>
  )
}

const renderCompanyDropdown = (companyItems: companyItemsType[]) => {
  return (
    <Container title='Use Cases' className='pl-10 pr-10 pt-6 pb-10 bg-[#f6f4fe]'>
      <div className='flex'>
        {companyItems.map((item, index) => (
          <div key={item.link ?? index} className='hover:text-gray-100'>
            <a
              href={item.link}
              className='
              flex
              items-center
              gap-2
              p-3
              text-lg
              font-normal
              text-neutral-800
              rounded-lg
              hover:bg-white
            '
            >
              {item.icon}
              <div className='flex flex-col'>
                <div className='flex'>
                  <p className='text-lg font-medium'>{item.title}</p>
                  {item.badge && (
                    <span className='text-sm font-normal bg-white text-[#3a2857] py-1 px-2 rounded-lg ml-2'>
                      {item.badge}
                    </span>
                  )}
                </div>
                <p className='text-base opacity-[.65]'>{item.description}</p>
              </div>
            </a>
          </div>
        ))}
      </div>
    </Container>
  )
}

const CampaignNavBarDropDown = ({ title, menuItems, url, type }: CampaignNavBarDropDownProps) => {
  return (
    <div className='group h-full max-w-7xl'>
      {/* Dropdown trigger */}
      <button
        className='
          relative
          flex
          h-full
          px-4
          items-center
          space-x-2
          text-xl
          font-normal
          text-neutral-800
          border-b-2
          border-transparent
          hover:border-b-purple-600
          hover:bg-gray-100
          transition
          duration-200
          group-hover:flex
        '
        onClick={() => {
          if (url) {
            window.location.href = url
          }
        }}
      >
        <span className='font-medium text-md'>{title}</span>
      </button>

      {/* Only render dropdown if we have items */}
      {menuItems && (
        <div
          className='
            absolute
            top-full
            left-0
            right-0
            w-screen
            hidden
            group-hover:flex
            flex-col
            shadow-lg
            rounded-lg
            z-50
          '
        >
          <div className='mx-auto w-full bg-[#f6f4fe]'>
            <div className='max-w-7xl mx-auto flex'>
              {type === 'product' && renderPlatformDropdown(platformMenuItems)}
              {type === 'usecases' && renderUseCasesDropdown()}
              {type === 'resources' && renderResourceDropdown(resourcesItems)}
              {type === 'company' && renderCompanyDropdown(companyItems)}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CampaignNavBarDropDown
